/** Legacy fixed query for OP doctor consultation line item; kept for name-based fallbacks. */
export const DOCTOR_CONSULTATION_SERVICE_QUERY = "Doctor Consultation";

/**
 * Visit type → medical-service name suffix (case-insensitive match).
 * New Visit → Consultation fees; Follow up → Follow up fees; Report → Report Visit fees.
 */
export const VISIT_TYPE_FEE_NAME_SUFFIX = {
  FIRST_VISIT: "consultation fees",
  FOLLOW_UP: "follow up fees",
  REPORT_VISIT: "report visit fees",
};

export function normalizeAppointmentVisitType(visitType) {
  const t = String(visitType || "")
    .trim()
    .toUpperCase();
  if (t === "FOLLOW_UP" || t === "REPORT_VISIT" || t === "FIRST_VISIT") return t;
  return "FIRST_VISIT";
}

export function getFeeNameSuffixForVisitType(visitType) {
  const key = normalizeAppointmentVisitType(visitType);
  return VISIT_TYPE_FEE_NAME_SUFFIX[key] || VISIT_TYPE_FEE_NAME_SUFFIX.FIRST_VISIT;
}

/**
 * Legacy: doctor-consultation services were named "{Doctor Name} Consultation fees".
 * Prefer `get_medical_services/?doctor_id=` instead.
 */
export function buildDoctorConsultationServiceQuery(doctorName, visitType) {
  const name = String(doctorName || "").trim();
  const suffixLabel =
    normalizeAppointmentVisitType(visitType) === "FOLLOW_UP"
      ? "Follow up fees"
      : normalizeAppointmentVisitType(visitType) === "REPORT_VISIT"
        ? "Report Visit fees"
        : "Consultation fees";
  return name ? `${name} ${suffixLabel}` : DOCTOR_CONSULTATION_SERVICE_QUERY;
}

function medicalServiceLabel(row) {
  return String(
    row?.name ?? row?.term ?? row?.matched_query ?? row?.service_name ?? "",
  )
    .trim()
    .toLowerCase();
}

/** Matches secured APIs: JWT + App-Mode, Org-Code, Username, User-Access-Key */
export function getSecuredApiHeaders(accessToken) {
  const token = accessToken || sessionStorage.getItem("accessToken") || "";
  const mode =
    sessionStorage.getItem("APP_MODE") ||
    sessionStorage.getItem("appMode") ||
    "";
  const org =
    sessionStorage.getItem("ORG_CODE") ||
    sessionStorage.getItem("orgCode") ||
    "";
  const username =
    sessionStorage.getItem("userName") ||
    sessionStorage.getItem("username") ||
    "";
  const accessKey =
    sessionStorage.getItem("USER_ACCESS_KEY") ||
    sessionStorage.getItem("accessKey") ||
    "";
  return {
    Authorization: `Bearer ${token}`,
    "App-Mode": mode,
    "Org-Code": org,
    Username: username,
    "User-Access-Key": accessKey,
  };
}

function mapMedicalServiceToConsultationRow(row) {
  if (!row) return null;
  const service_id = row?.medical_service_id ?? row?.service_id;
  if (service_id == null || String(service_id).trim() === "") return null;

  const priceRaw = row?.fee ?? row?.charges ?? row?.price ?? 0;
  const price = Number(priceRaw);

  return {
    item_id: row?.id ?? row?.item_id ?? null,
    service_id: String(service_id).trim(),
    service_name:
      row?.name ?? row?.term ?? row?.matched_query ?? row?.service_name,
    price: Number.isFinite(price) ? price : 0,
  };
}

/**
 * Pick a doctor fee service row from `get_medical_services/` results.
 * When `targetQuery` is set, requires an exact name match (legacy name search).
 * Otherwise matches `visitType` against the service name suffix
 * (Consultation / Follow up / Report Visit fees), preferring
 * `is_doctor_appointment_charge` rows.
 */
export function pickDoctorConsultationRow(rows, targetQuery, visitType) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const validRows = rows.filter(
    (r) => r?.active !== false && r?.expired !== true,
  );
  // Do not fall back to inactive/expired rows — block booking/invoice instead.
  if (!validRows.length) return null;
  const pool = validRows;

  let row = null;
  if (targetQuery) {
    const target = String(targetQuery).toLowerCase();
    row = pool.find((r) => medicalServiceLabel(r) === target) || null;
  } else {
    const suffix = getFeeNameSuffixForVisitType(visitType);
    const appointmentCharges = pool.filter(
      (r) => r?.is_doctor_appointment_charge === true,
    );
    const searchPool = appointmentCharges.length ? appointmentCharges : pool;
    row =
      searchPool.find((r) => medicalServiceLabel(r).includes(suffix)) || null;

    // Legacy single-service doctors: New Visit may fall back to any appointment charge.
    if (!row && normalizeAppointmentVisitType(visitType) === "FIRST_VISIT") {
      row =
        searchPool.find((r) =>
          medicalServiceLabel(r).includes("consultation fee"),
        ) ||
        appointmentCharges[0] ||
        null;
    }
  }

  return mapMedicalServiceToConsultationRow(row);
}

/**
 * `create_appointment_invoice` `item.item_id` — backend accepts UUID (`medical_service_id`)
 * first; falls back to numeric PK when UUID is unavailable.
 */
export function resolveAppointmentInvoiceItemId(serviceRow) {
  if (!serviceRow) return null;
  if (serviceRow.service_id != null && String(serviceRow.service_id).trim()) {
    return String(serviceRow.service_id).trim();
  }
  const pk = serviceRow.item_id;
  if (pk != null && pk !== "") {
    const n = Number(pk);
    if (Number.isFinite(n) && n > 0) return n;
    const s = String(pk).trim();
    if (s) return s;
  }
  return null;
}

export function formatInvoiceDateYmd(appointmentStart) {
  const d = new Date(appointmentStart);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function isInvoiceDateNotBeforeToday(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const inv = new Date(`${ymd}T00:00:00`);
  return !Number.isNaN(inv.getTime()) && inv >= today;
}

/**
 * The resolved "{Doctor} Consultation fees" medical-service row is the sole
 * source of truth for the consultation price — it's the same record whose
 * id is sent as the invoice line item, so its `fee` must be what's charged.
 * (Previously preferred a separate doctor-level `fees` field, which could
 * silently drift out of sync with the billing catalog.)
 */
export function getConsultationUnitPriceStr(
  doctorBusinessId,
  doctorsList,
  serviceCatalogPrice,
) {
  if (serviceCatalogPrice == null || String(serviceCatalogPrice).trim() === "") {
    return "0.00";
  }
  const fromCatalog = Number(serviceCatalogPrice);
  // 0 is valid (Follow-up / Report Visit often have zero fee).
  if (Number.isFinite(fromCatalog) && fromCatalog >= 0) {
    return fromCatalog.toFixed(2);
  }
  return "0.00";
}

export function safeInvoiceNumber(val) {
  const n = Number(val);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Returns price string for invoice create, or `null` if catalog price is missing/invalid.
 * Explicit 0.00 is valid — Follow-up and Report Visit fees may be configured as zero.
 */
export function resolveConsultationUnitPriceStr(
  doctorBusinessId,
  doctorsList,
  serviceCatalogPrice,
) {
  if (serviceCatalogPrice == null || String(serviceCatalogPrice).trim() === "") {
    return null;
  }
  const fromCatalog = Number(serviceCatalogPrice);
  if (!Number.isFinite(fromCatalog) || fromCatalog < 0) {
    return null;
  }
  return fromCatalog.toFixed(2);
}

/** Load facility doctors (fees) before creating appointment invoice. */
export async function fetchFacilityDoctorsMap(axios, baseUrl, headers) {
  try {
    const response = await axios.get(
      `${baseUrl}get_facility_specific_doctors/`,
      { headers },
    );
    if (
      response?.data?.status === "Success" &&
      Array.isArray(response.data.doctor_list)
    ) {
      const map = {};
      response.data.doctor_list.forEach((doc) => {
        if (doc?.doctor_id) map[doc.doctor_id] = doc;
      });
      return map;
    }
  } catch {
    // caller handles missing map
  }
  return {};
}

export function isInvoiceLineAmountEmpty(item = {}) {
  const price = safeInvoiceNumber(
    item?.item_price ?? item?.price ?? item?.rate,
  );
  const sub = safeInvoiceNumber(item?.subtotal ?? item?.amount);
  const gross = safeInvoiceNumber(item?.gross);
  return price <= 0 && sub <= 0 && gross <= 0;
}

/** Invoice header total (excludes payment-only fields when header is set). */
export function getInvoiceHeaderTotal(invoice = {}) {
  return safeInvoiceNumber(
    invoice?.total_amount ??
      invoice?.totalAmount ??
      invoice?.total ??
      invoice?.payable_amount ??
      0,
  );
}

const BLOCKED_PAYMENT_STATUS = new Set([
  "FAILED",
  "FAILURE",
  "CANCELLED",
  "CANCELED",
]);

function isSettlementPaymentRow(payment) {
  const paymentType = String(payment?.payment_type || "").toUpperCase();
  if (paymentType === "REFUND") return false;
  const status = String(payment?.status || "").toUpperCase();
  if (status && BLOCKED_PAYMENT_STATUS.has(status)) return false;
  return true;
}

/** One payment list only — avoids doubling `payment_details` + `payments` + `payment`. */
export function collectInvoicePayments(invoice = {}) {
  let rows = [];
  if (
    Array.isArray(invoice?.payment_details) &&
    invoice.payment_details.length > 0
  ) {
    rows = invoice.payment_details;
  } else if (Array.isArray(invoice?.payments) && invoice.payments.length > 0) {
    rows = invoice.payments;
  } else if (invoice?.payment) {
    rows = [invoice.payment];
  }

  const seen = new Set();
  const out = [];
  for (const p of rows) {
    if (!p || !isSettlementPaymentRow(p)) continue;
    const key =
      p.payment_id != null
        ? `id:${p.payment_id}`
        : p.receipt_number
          ? `rcpt:${String(p.receipt_number).trim()}`
          : `row:${p.created_at}-${p.payment_amount}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
}

export function getInvoicePaymentTotal(invoice = {}) {
  return collectInvoicePayments(invoice).reduce(
    (sum, p) => sum + safeInvoiceNumber(p?.payment_amount ?? p?.amount),
    0,
  );
}

/**
 * When line rows are all zero but invoice/payment has an amount (display-only patch).
 */
export function getLineItemFallbackTotal(invoice = {}, options = {}) {
  const header = getInvoiceHeaderTotal(invoice);
  if (header > 0) return header;

  const paid = getInvoicePaymentTotal(invoice);
  if (paid > 0) return paid;

  const hint = safeInvoiceNumber(
    options?.paymentAmountHint ?? invoice?.amount_paid,
  );
  if (hint > 0) return hint;

  return safeInvoiceNumber(invoice?.partial_payment);
}

/**
 * Patch raw API rows before ViewInvoice loads (Process Payment → preview).
 */
export function enrichLineItemsFromInvoiceTotals(
  lineItems,
  invoice = {},
  options = {},
) {
  if (!Array.isArray(lineItems) || lineItems.length === 0) return lineItems;
  const emptyCount = lineItems.filter(isInvoiceLineAmountEmpty).length;
  if (emptyCount === 0) return lineItems;

  const fallback = getLineItemFallbackTotal(invoice, options);
  if (fallback <= 0) return lineItems;

  return lineItems.map((row) => {
    if (!isInvoiceLineAmountEmpty(row)) return row;
    const qty = safeInvoiceNumber(row?.quantity ?? row?.qty ?? 1) || 1;
    const lineTotal =
      lineItems.length === 1 ? fallback : fallback / lineItems.length;
    const unit = lineTotal / qty;
    return {
      ...row,
      item_price: unit,
      price: unit,
      gross: lineTotal,
      subtotal: lineTotal,
      amount: lineTotal,
    };
  });
}

/**
 * Normalizes invoice line items for display/PDF (Price, Gross, Discount, Amount).
 * When APIs return `item_price: 0` but `subtotal` is set (e.g. appointment invoices),
 * unit price and gross are derived from subtotal or gross — not by service name.
 *
 * @param {object} item Raw line from `get_all_invoices` / `itemsList`
 * @param {{ resolveItemName?: (serviceId: string|number) => string }} [options]
 */
export function normalizeInvoiceLineItem(item = {}, options = {}) {
  const {
    resolveItemName,
    invoiceContext = null,
    lineCount = 0,
    paymentAmountHint,
  } = options;
  const serviceId = item?.items || item?.item_id || item?.service_id;
  const resolvedName =
    typeof resolveItemName === "function" ? resolveItemName(serviceId) : "";
  const item_name =
    item?.item_name ||
    item?.itemName ||
    item?.service_name ||
    item?.name ||
    item?.description ||
    resolvedName ||
    "--";

  const quantity = safeInvoiceNumber(item?.quantity ?? item?.qty ?? 0);
  const qtyForMath = quantity > 0 ? quantity : 1;

  let item_price = safeInvoiceNumber(
    item?.item_price ?? item?.price ?? item?.rate ?? 0,
  );
  let gross = safeInvoiceNumber(item?.gross);
  let subtotal = safeInvoiceNumber(item?.subtotal ?? item?.amount ?? 0);

  if (item_price <= 0 && qtyForMath > 0) {
    if (gross > 0) {
      item_price = gross / qtyForMath;
    } else if (subtotal > 0) {
      item_price = subtotal / qtyForMath;
    }
  }

  if (gross <= 0 && item_price > 0) {
    gross = item_price * qtyForMath;
  }
  if (gross <= 0 && subtotal > 0) {
    gross = subtotal;
  }
  if (subtotal <= 0) {
    subtotal = safeInvoiceNumber(item?.subtotal ?? item?.amount ?? gross);
  }

  const pctRaw =
    item?.discount_percentage ??
    item?.discount_percent ??
    item?.discount_pct ??
    null;

  let discount_amount = safeInvoiceNumber(
    item?.discount_amount ?? item?.line_discount ?? item?.discount_value ?? 0,
  );
  if (
    discount_amount === 0 &&
    pctRaw != null &&
    safeInvoiceNumber(pctRaw) > 0
  ) {
    discount_amount = gross * (safeInvoiceNumber(pctRaw) / 100);
  }
  if (discount_amount === 0 && gross > 0 && subtotal >= 0 && gross > subtotal) {
    discount_amount = gross - subtotal;
  }
  if (
    discount_amount === 0 &&
    item?.discount != null &&
    item?.discount !== ""
  ) {
    const legacyDiscount = safeInvoiceNumber(item.discount);
    if (legacyDiscount > 0 && legacyDiscount <= 100 && gross > 0) {
      discount_amount = gross * (legacyDiscount / 100);
    } else {
      discount_amount = legacyDiscount;
    }
  }

  let discount_percent = safeInvoiceNumber(pctRaw);
  if (discount_percent === 0 && gross > 0 && discount_amount > 0) {
    discount_percent = (discount_amount / gross) * 100;
  }

  const tax = safeInvoiceNumber(item?.tax ?? item?.tax_amount ?? 0);

  const lineStillEmpty = item_price <= 0 && gross <= 0 && subtotal <= 0;
  const effectiveLineCount = lineCount > 0 ? lineCount : 1;
  if (lineStillEmpty && invoiceContext && effectiveLineCount === 1) {
    const hint =
      getInvoicePaymentTotal(invoiceContext) > 0
        ? undefined
        : paymentAmountHint;
    const fallbackTotal = getLineItemFallbackTotal(invoiceContext, {
      paymentAmountHint: hint,
    });
    if (fallbackTotal > 0) {
      item_price = fallbackTotal / qtyForMath;
      gross = fallbackTotal;
      subtotal = fallbackTotal;
    }
  }

  return {
    item_name,
    serviceId,
    quantity,
    item_price,
    gross,
    discount_amount,
    discount_percent,
    tax,
    subtotal,
  };
}

/**
 * Fetch the doctor's fee medical-service row via
 * `POST get_medical_services/?doctor_id=<uuid>`, matched to `visitType`.
 */
export async function fetchDoctorConsultationServiceRow(
  axios,
  baseUrl,
  headers,
  doctorId,
  visitType = "FIRST_VISIT",
) {
  const id = doctorId != null ? String(doctorId).trim() : "";
  if (!id) return null;

  const response = await axios.post(
    `${baseUrl}get_medical_services/?doctor_id=${encodeURIComponent(id)}`,
    {},
    {
      headers: {
        ...headers,
        "Content-Type": "application/json",
      },
    },
  );
  const ok = String(response?.data?.status || "").toLowerCase() === "success";
  const rows = Array.isArray(response?.data?.data)
    ? response.data.data
    : Array.isArray(response?.data?.service_list)
      ? response.data.service_list
      : [];
  if (!ok || !rows.length) return null;
  return pickDoctorConsultationRow(rows, null, visitType);
}

/** True when a resolved service row's name matches the visit-type fee suffix. */
export function doesServiceRowMatchVisitType(serviceRow, visitType) {
  if (!serviceRow) return false;
  const label = String(serviceRow.service_name || "").toLowerCase();
  const suffix = getFeeNameSuffixForVisitType(visitType);
  return label.includes(suffix);
}
